import { isQuotableAmount } from '@funkit/connect-core'
import { logger } from '../logging/logger'
import type {
  DepositFlowConfig,
  DepositInitialAmount,
} from './depositFlowConfig'

const ISO_4217_CODE = /^[A-Z]{3}$/

/**
 * Drops an invalid initialAmount/lockAmount instead of failing the whole
 * flow — mirrors `validateClientMetadata`. `lockAmount` requires a valid
 * `initialAmount`; without one it is dropped too, since there would be
 * nothing to lock.
 */
export function validateInitialAmount({
  initialAmount,
  lockAmount,
}: Pick<DepositFlowConfig, 'initialAmount' | 'lockAmount'>): Pick<
  DepositFlowConfig,
  'initialAmount' | 'lockAmount'
> {
  let validAmount: DepositInitialAmount | undefined
  if (initialAmount !== undefined) {
    // Untyped hosts reach here with malformed shapes, which `isQuotableAmount`
    // throws on rather than rejects.
    const hasValidShape =
      typeof initialAmount === 'object' && initialAmount !== null
    const hasValidValue =
      hasValidShape &&
      typeof initialAmount.value === 'string' &&
      isQuotableAmount(initialAmount.value)
    const hasValidCurrency =
      hasValidShape &&
      typeof initialAmount.currency === 'string' &&
      ISO_4217_CODE.test(initialAmount.currency)

    if (hasValidValue && hasValidCurrency) {
      validAmount = initialAmount
    } else {
      logger.warn('connect-rn:deposit:invalid_initial_amount', {
        field: 'initialAmount',
        value: initialAmount,
      })
    }
  }

  let validLockAmount: true | undefined
  if (lockAmount === true) {
    if (validAmount !== undefined) {
      validLockAmount = true
    } else {
      logger.warn('connect-rn:deposit:invalid_initial_amount', {
        field: 'lockAmount',
        value: lockAmount,
      })
    }
  } else if (lockAmount !== undefined && lockAmount !== false) {
    logger.warn('connect-rn:deposit:invalid_lock_amount', {
      field: 'lockAmount',
      value: lockAmount,
    })
  }

  return {
    initialAmount: validAmount,
    lockAmount: validLockAmount,
  }
}
